import React, { useState } from 'react';
import { View, Text, Pressable } from 'react-native';
import { ChevronDown, ChevronUp } from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';

interface RecommendationsSectionProps {
  recommendations: string[];
  /** When true, render all recommendations expanded with no chevron / preview /
   *  collapse toggle. Use when the parent already provides a collapse layer. */
  alwaysExpanded?: boolean;
}

function NumberBadge({ n, colors }: { n: number; colors: any }) {
  return (
    <View
      style={{
        width: 22,
        height: 22,
        borderRadius: 11,
        backgroundColor: colors.primary[100],
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: 10,
        flexShrink: 0,
      }}
    >
      <Text
        style={{
          color: colors.primary[700],
          fontSize: 11,
          fontWeight: '700',
          lineHeight: 14,
        }}
      >
        {n}
      </Text>
    </View>
  );
}

function RecommendationRow({ index, text, colors }: { index: number; text: string; colors: any }) {
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'flex-start',
        paddingVertical: 6,
      }}
    >
      <NumberBadge n={index} colors={colors} />
      <Text
        style={{
          color: colors.text.primary,
          fontSize: 13,
          lineHeight: 18,
          flex: 1,
        }}
      >
        {text}
      </Text>
    </View>
  );
}

export function RecommendationsSection({ recommendations, alwaysExpanded = false }: RecommendationsSectionProps) {
  const colors = useThemeColors();
  const [expanded, setExpanded] = useState<boolean>(false);

  if (recommendations.length === 0) return null;

  // When the parent already provides a collapse layer, render the full list
  // with no chrome (no chevron, no preview, no toggle).
  if (alwaysExpanded) {
    return (
      <View>
        {recommendations.map((rec, i) => (
          <RecommendationRow key={`rec-${i}`} index={i + 1} text={rec} colors={colors} />
        ))}
      </View>
    );
  }

  const hasMore = recommendations.length > 1;
  const Chevron = expanded ? ChevronUp : ChevronDown;

  return (
    <Pressable
      onPress={hasMore ? () => setExpanded((v) => !v) : undefined}
      style={{
        backgroundColor: colors.primary[50],
        borderWidth: 1,
        borderColor: colors.primary[100],
        borderRadius: 12,
        padding: 14,
        marginBottom: 4,
      }}
    >
      {/* Chevron at top-right */}
      {hasMore && (
        <View
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            zIndex: 1,
          }}
        >
          <Chevron size={16} color={colors.primary[700]} />
        </View>
      )}

      {!expanded ? (
        <View style={{ paddingRight: hasMore ? 24 : 0 }}>
          <RecommendationRow index={1} text={recommendations[0]} colors={colors} />
          {hasMore && (
            <Text
              style={{
                color: colors.primary[700],
                fontSize: 11,
                fontWeight: '600',
                marginTop: 6,
                marginLeft: 32,
              }}
            >
              + {recommendations.length - 1} more action{recommendations.length - 1 !== 1 ? 's' : ''}
            </Text>
          )}
        </View>
      ) : (
        <View style={{ paddingRight: 24 }}>
          {recommendations.map((rec, i) => (
            <RecommendationRow key={`rec-${i}`} index={i + 1} text={rec} colors={colors} />
          ))}
        </View>
      )}
    </Pressable>
  );
}
